import { Action, createReducer, on } from '@ngrx/store';
import { IOffering, IOfferingDetail, IOfferingDetailItem } from 'app/models';
import {
    getDetailTypeName,
    initialMotivationalNudgeState,
    initialOfferingContactsState,
    initialPartnerUsersState,
    initOfferingState,
    intiUserSearchState,
    IMotivationalNudgeState,
    IOfferingContactsState,
    IOfferingsState,
    IPartnerAccountingTotals,
    IPartnerLedgerItem,
    IPartnerThemeView,
    IPartnerUsersState,
    IPayoutDetail,
    ITotals,
    IUserSearchState,
    moveItem,
    newClientId,
    resequenceDetails,
    resequenceItems,
    upsertOfferingSummary,
} from '../models';
import { IPartnerBase } from '../models/partner-base';
import * as actions from './actions';
import { AdminUserSearchActions, MotivationalNudgeActions, OfferingContactsActions, PartnerUsersActions } from './actions';

export interface AdministrationState {
    totals: ITotals | undefined;
    payoutDetails: IPayoutDetail[];
    payoutDocumentUrl: string;
    partners: IPartnerBase[];
    partnerTotals: IPartnerAccountingTotals;
    partnerLedgerItems: IPartnerLedgerItem[];
    partnerTheme: IPartnerThemeView;
    partnerOfferings: IOffering[];
    partnerOffering: IOffering;
    offeringState: IOfferingsState;
    partnerUserState: IPartnerUsersState;
    userSearchState: IUserSearchState;
    offeringContactsState: IOfferingContactsState;
    motivationalNudgeState: IMotivationalNudgeState;
}

export const initialState: AdministrationState = {
    totals: undefined,
    payoutDetails: undefined,
    payoutDocumentUrl: undefined,
    partners: undefined,
    partnerTotals: undefined,
    partnerLedgerItems: undefined,
    partnerTheme: undefined,
    partnerOfferings: undefined,
    partnerOffering: undefined,
    offeringState: initOfferingState,
    partnerUserState: initialPartnerUsersState,
    userSearchState: intiUserSearchState,
    offeringContactsState: initialOfferingContactsState,
    motivationalNudgeState: initialMotivationalNudgeState,
};

const administrationReducer = createReducer(
    initialState,
    on(actions.getPaymentQueue, (state, payload) => {
        return {
            ...state,
            payoutDetails: payload.details,
        };
    }),
    on(actions.clearPaymentQueue, (state) => {
        return {
            ...state,
            coverage: null,
        };
    }),
    on(actions.getTotals, (state, payload) => {
        return {
            ...state,
            totals: payload.totals,
        };
    }),
    on(actions.clearTotals, (state) => {
        return {
            ...state,
            totals: undefined,
        };
    }),
    on(actions.getPayoutDocument, (state, payload) => {
        return {
            ...state,
            payoutDocumentUrl: payload.documentUrl,
        };
    }),
    on(actions.clearPayoutDocument, (state) => {
        return {
            ...state,
            payoutDocumentUrl: undefined,
        };
    }),
    on(actions.getPartners, (state, payload) => {
        return {
            ...state,
            partners: payload.partners,
        };
    }),
    on(actions.clearPartners, (state) => {
        return {
            ...state,
            partners: undefined,
        };
    }),
    on(actions.getPartnerAccoutingTotals, (state, payload) => {
        return {
            ...state,
            partnerTotals: payload.totals,
        };
    }),
    on(actions.clearPartnerAccoutingTotals, (state) => {
        return {
            ...state,
            partnerTotals: undefined,
        };
    }),
    on(actions.getPartnerAccoutingItems, (state, payload) => {
        return {
            ...state,
            partnerLedgerItems: payload.items,
        };
    }),
    on(actions.clearPartnerAccoutingItems, (state) => {
        return {
            ...state,
            partnerLedgerItems: undefined,
        };
    }),
    on(actions.getTheme, (state, payload) => {
        return {
            ...state,
            partnerTheme: payload.theme,
        };
    }),
    on(actions.clearTheme, (state) => {
        return {
            ...state,
            partnerTheme: undefined,
        };
    }),
    on(actions.saveThemeSuccess, (state, payload) => {
        return {
            ...state,
            partnerTheme: payload.theme,
        };
    }),

    on(actions.loadOfferings, (state, { companyId }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            companyId,
            offeringsLoading: true,
            error: null,
        },
    })),

    on(actions.loadOfferingsSuccess, (state, { offerings }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            offerings,
            offeringsLoading: false,
            error: null,
        },
    })),

    on(actions.loadOfferingsFailure, (state, { error }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            offeringsLoading: false,
            error,
        },
    })),

    on(actions.loadOffering, (state, { offeringId }) => ({
        ...state,
        selectedOfferingId: offeringId,
        selectedOfferingLoading: true,
        error: null,
    })),

    on(actions.loadOfferingSuccess, (state, { offering }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            selectedOffering: offering,
            selectedOfferingId: offering.id,
            selectedOfferingLoading: false,
            dirty: false,
            error: null,
        },
    })),

    on(actions.loadOfferingFailure, (state, { error }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            selectedOfferingLoading: false,
            error,
        },
    })),

    on(actions.createNewOfferingDraft, (state, { companyId, nextSortOrder }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            selectedOfferingId: 0,
            selectedOffering: {
                id: 0,
                companyId,
                name: '',
                description: '',
                rateIntro: null,
                rateCloseMin: null,
                rateCloseMax: null,
                isActive: true,
                sortOrder: nextSortOrder,
                details: [],
            },
            dirty: true,
            error: null,
        },
    })),

    on(actions.createOffering, (state) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            ...state,
            saving: true,
            error: null,
        },
    })),

    on(actions.createOfferingSuccess, (state, { offering }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            saving: false,
            selectedOffering: offering,
            selectedOfferingId: offering.id,
            offerings: upsertOfferingSummary(state.offeringState.offerings, offering),
            dirty: false,
            error: null,
        },
    })),

    on(actions.createOfferingFailure, (state, { error }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            ...state,
            saving: false,
            error,
        },
    })),

    on(actions.saveOffering, (state) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            ...state,
            saving: true,
            error: null,
        },
    })),

    on(actions.saveOfferingSuccess, (state, { offering }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            saving: false,
            selectedOffering: offering,
            selectedOfferingId: offering.id,
            offerings: upsertOfferingSummary(state.offeringState.offerings, offering),
            dirty: false,
            error: null,
        },
    })),

    on(actions.saveOfferingFailure, (state, { error }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            saving: false,
            error,
        },
    })),

    on(actions.deleteOffering, (state) => ({
        ...state,
        deleting: true,
        error: null,
    })),

    on(actions.deleteOfferingSuccess, (state, { offeringId }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            deleting: false,
            offerings: state.offeringState.offerings.filter((x) => x.id !== offeringId),
            selectedOffering: state.offeringState?.selectedOffering?.id === offeringId ? null : state.offeringState?.selectedOffering,
            selectedOfferingId: state.offeringState?.selectedOfferingId === offeringId ? null : state.offeringState?.selectedOfferingId,
            dirty: false,
            error: null,
        },
    })),

    on(actions.deleteOfferingFailure, (state, { error }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            deleting: false,
            error,
        },
    })),

    on(actions.updateOfferingField, (state, { changes }) => ({
        ...state,
        offeringState: {
            ...state.offeringState,
            selectedOffering: state.offeringState?.selectedOffering
                ? {
                      ...state.offeringState?.selectedOffering,
                      ...changes,
                  }
                : null,
            dirty: true,
        },
    })),

    on(actions.addDetail, (state, { detailTypeId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const newDetail: IOfferingDetail = {
            id: newClientId(),
            offeringId: state.offeringState?.selectedOffering.id,
            detailTypeId,
            detailTypeName: getDetailTypeName(detailTypeId),
            sortOrder: state.offeringState?.selectedOffering.details.length + 1,
            items: [],
        };

        const details = resequenceDetails([...state.offeringState?.selectedOffering.details, newDetail]);

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.removeDetail, (state, { detailId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = resequenceDetails(state.offeringState?.selectedOffering.details.filter((detail) => detail.id !== detailId));

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.moveDetailUp, (state, { detailId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const index = state.offeringState?.selectedOffering.details.findIndex((x) => x.id === detailId);
        if (index <= 0) {
            return state;
        }

        const details = resequenceDetails(moveItem(state.offeringState?.selectedOffering.details, index, index - 1));

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.moveDetailDown, (state, { detailId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const index = state.offeringState?.selectedOffering.details.findIndex((x) => x.id === detailId);
        if (index === -1 || index >= state.offeringState?.selectedOffering.details.length - 1) {
            return state;
        }

        const details = resequenceDetails(moveItem(state.offeringState?.selectedOffering.details, index, index + 1));

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.addDetailItem, (state, { detailId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = state.offeringState?.selectedOffering.details.map((detail) => {
            if (detail.id !== detailId) {
                return detail;
            }

            const newItem: IOfferingDetailItem = {
                id: newClientId(),
                /* detailId: detail.id, */
                sortOrder: detail.items.length + 1,
                text: '',
            };

            return {
                ...detail,
                items: resequenceItems([...detail.items, newItem]),
            };
        });

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.updateDetailItem, (state, { detailId, itemId, text }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = state.offeringState?.selectedOffering.details.map((detail) => {
            if (detail.id !== detailId) {
                return detail;
            }

            const items = detail.items.map((item) =>
                item.id === itemId
                    ? {
                          ...item,
                          text,
                      }
                    : item
            );

            return {
                ...detail,
                items,
            };
        });

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.removeDetailItem, (state, { detailId, itemId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = state.offeringState?.selectedOffering.details.map((detail) => {
            if (detail.id !== detailId) {
                return detail;
            }

            const items = resequenceItems(detail.items.filter((item) => item.id !== itemId));

            return {
                ...detail,
                items,
            };
        });

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.moveDetailItemUp, (state, { detailId, itemId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = state.offeringState?.selectedOffering.details.map((detail) => {
            if (detail.id !== detailId) {
                return detail;
            }

            const index = detail.items.findIndex((x) => x.id === itemId);
            if (index <= 0) {
                return detail;
            }

            return {
                ...detail,
                items: resequenceItems(moveItem(detail.items, index, index - 1)),
            };
        });

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.moveDetailItemDown, (state, { detailId, itemId }) => {
        if (!state.offeringState?.selectedOffering) {
            return state;
        }

        const details = state.offeringState?.selectedOffering.details.map((detail) => {
            if (detail.id !== detailId) {
                return detail;
            }

            const index = detail.items.findIndex((x) => x.id === itemId);
            if (index === -1 || index >= detail.items.length - 1) {
                return detail;
            }

            return {
                ...detail,
                items: resequenceItems(moveItem(detail.items, index, index + 1)),
            };
        });

        return {
            ...state,
            offeringState: {
                ...state.offeringState,
                selectedOffering: {
                    ...state.offeringState?.selectedOffering,
                    details,
                },
                dirty: true,
            },
        };
    }),

    on(actions.clearSelectedOffering, (state) => ({
        ...state,
        offeringState: initOfferingState,
    })),

    on(PartnerUsersActions.loadCompanyUsers, (state, { companyId }) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            companyId,
            loading: true,
            error: null,
        },
    })),

    on(PartnerUsersActions.loadCompanyUsersSuccess, (state, { companyId, users }) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            companyId,
            users,
            loading: false,
        },
    })),

    on(PartnerUsersActions.loadCompanyUsersFailure, (state, { error }) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            loading: false,
            error,
        },
    })),

    on(PartnerUsersActions.addCompanyUser, PartnerUsersActions.removeCompanyUser, (state) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            saving: true,
            error: null,
        },
    })),

    on(PartnerUsersActions.addCompanyUserSuccess, PartnerUsersActions.removeCompanyUserSuccess, (state) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            saving: false,
        },
    })),

    on(PartnerUsersActions.addCompanyUserFailure, PartnerUsersActions.removeCompanyUserFailure, (state, { error }) => ({
        ...state,
        partnerUserState: {
            ...state.partnerUserState,
            saving: false,
            error,
        },
    })),
    on(PartnerUsersActions.clearCompanyUsers, (state) => ({
        ...state,
        partnerUserState: initialPartnerUsersState,
    })),

    on(AdminUserSearchActions.searchUsersSuccess, (state, { query, results }) => ({
        ...state,
        userSearchState: {
            ...state.userSearchState,
            query,
            results,
            loading: false,
        },
    })),
    on(AdminUserSearchActions.searchUsersFailure, (state, { error }) => ({
        ...state,
        userSearchState: {
            ...state.userSearchState,
            loading: false,
            error,
        },
    })),

    on(AdminUserSearchActions.clearSearchResults, (state) => ({
        ...state,
        userSearchState: intiUserSearchState,
    })),

    on(MotivationalNudgeActions.sendNudge, (state) => ({
        ...state,
        motivationalNudgeState: {
            ...state.motivationalNudgeState,
            sending: true,
            error: null,
        },
    })),
    on(MotivationalNudgeActions.sendNudgeSuccess, (state) => ({
        ...state,
        motivationalNudgeState: {
            ...state.motivationalNudgeState,
            sending: false,
        },
    })),
    on(MotivationalNudgeActions.sendNudgeFailure, (state, { error }) => ({
        ...state,
        motivationalNudgeState: {
            ...state.motivationalNudgeState,
            sending: false,
            error,
        },
    })),

    on(OfferingContactsActions.loadOfferingContacts, (state, { offeringId }) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            offeringId,
            loading: true,
            error: null,
        },
    })),

    on(OfferingContactsActions.loadOfferingContactsSuccess, (state, { offeringId, contacts }) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            offeringId,
            contacts,
            loading: false,
            error: null,
        },
    })),

    on(OfferingContactsActions.loadOfferingContactsFailure, (state, { error }) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            loading: false,
            error,
        },
    })),

    on(OfferingContactsActions.addOfferingContact, OfferingContactsActions.updateOfferingContact, OfferingContactsActions.deleteOfferingContact, (state) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            saving: true,
            error: null,
        },
    })),

    on(OfferingContactsActions.addOfferingContactSuccess, OfferingContactsActions.updateOfferingContactSuccess, OfferingContactsActions.deleteOfferingContactSuccess, (state) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            saving: false,
        },
    })),

    on(OfferingContactsActions.addOfferingContactFailure, OfferingContactsActions.updateOfferingContactFailure, OfferingContactsActions.deleteOfferingContactFailure, (state, { error }) => ({
        ...state,
        offeringContactsState: {
            ...state.offeringContactsState,
            saving: false,
            error,
        },
    })),

    on(OfferingContactsActions.clearOfferingContacts, (state) => ({
        ...state,
        offeringContactsState: initialOfferingContactsState,
    }))
);

export function reducer(state: AdministrationState | undefined, action: Action) {
    return administrationReducer(state, action);
}
